/**
 * Service for authentication functionality
 */

import { auth } from '@/firebase/index';
import {
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';

class AuthService {
  /**
   * Login a user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} - Promise with the logged in user
   */
  async login(email, password) {
    try {
      const response = await signInWithEmailAndPassword(auth, email, password);
      return {
        user: response.user,
        uid: response.user.uid
      };
    } catch (error) {
      throw new Error("Wrong email or password.");
    }
  }

  /**
   * Logout the current user
   * @returns {Promise} - Promise that resolves when logout is complete
   */
  async logout() {
    try {
      await signOut(auth);
      return true;
    } catch (error) {
      console.error("Error logging out:", error);
      throw error;
    }
  }

  /**
   * Set up an auth state changed listener
   * @param {Function} callback - Function to call when auth state changes
   * @returns {Function} - Function to unsubscribe from the listener
   */
  onAuthStateChanged(callback) {
    return auth.onAuthStateChanged(callback);
  }

  /**
   * Get the current user
   * @returns {Object|null} - Current user or null if not logged in
   */
  getCurrentUser() {
    return auth.currentUser;
  }

  /**
   * Get the current user's Firebase ID token (for Authorization headers).
   * @returns {Promise<string|null>} - The ID token, or null if not logged in
   */
  async getIdToken() {
    const user = auth.currentUser;
    if (!user) return null;
    return user.getIdToken();
  }
}

export default new AuthService();